import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike } from '@krp/client-runtime'

import { makeQueryViewSelector, readyData } from '../../../shared/model/query'
import { formatInstant } from '../../../shared/uc1b/format'
import { CURSOR_ERROR_CODES } from '../../receipts/model/selectors'
import type { AqsiSession } from '../../rmk/model/types'
import type { StateBadgeVm } from '../../sale/model/types'
import { DISCREPANCY_LIMITS, INCIDENTS_PAGE_SIZE } from '../api/incidentsApi'
import type {
  AqsiDiscrepancies,
  AqsiIncident,
  AqsiIncidentPage,
  AqsiRecoveryProof,
  DiscrepanciesVm,
  IncidentRowVm,
  IncidentsView,
  IncidentsVm,
  RecoveryProofVm,
} from './types'

type Badge = Omit<StateBadgeVm, 'label'>

/** Состояния денег и чека словами и значком: неизвестное значение договора показывается как пришло. */
const MONEY: Readonly<Record<string, Badge>> = {
  NotRequired: { text: 'наличные — терминал не нужен', tone: 'neutral', icon: 'idle' },
  Pending: { text: 'не начаты', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'идут на терминале', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'проведены', tone: 'success', icon: 'ok' },
  Failed: { text: 'не прошли', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'исход неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
}

const RECEIPT: Readonly<Record<string, Badge>> = {
  Pending: { text: 'не пробит', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'пробивается', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'пробит', tone: 'success', icon: 'ok' },
  Failed: { text: 'касса отказала', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'исход неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
  SetAside: { text: 'отложен', tone: 'warning', icon: 'idle' },
  IssuedOutside: { text: 'пробит мимо приложения', tone: 'warning', icon: 'ok' },
}

function badgeOf(table: Readonly<Record<string, Badge>>, label: string, value: string): StateBadgeVm {
  return { label, ...(table[value] ?? { text: value, tone: 'warning', icon: 'unknown' }) }
}

function incidentRow(item: AqsiIncident): IncidentRowVm {
  const attention = item.payment === 'Unknown' || item.fiscal === 'Unknown' || item.hasUnresolvedAttempt
  const notes: string[] = []
  if (item.hasUnresolvedAttempt) notes.push('Попытка у кабинета не разрешена: нужна сверка, повтор запрещён')
  if (item.legacyIsFinished) notes.push('Отмечена завершённой, но журнал операций ещё не закрыт')
  return {
    saleId: item.saleId,
    createdText: formatInstant(item.createdAt),
    attention,
    attentionText: attention ? 'Требует сверки' : 'Не завершена',
    money: badgeOf(MONEY, 'Деньги', item.payment),
    receipt: badgeOf(RECEIPT, 'Чек', item.fiscal),
    notes,
  }
}

/** Касса моей работы: журнал кассы читает только тот, кто за ней работает (C4b, 403 иначе). */
export function incidentsSeatOf(session: AqsiSession | null | undefined): { registerKey: string; deviceId: number } | null {
  return session == null ? null : { registerKey: session.registerKey, deviceId: session.deviceId }
}

export interface IncidentsVmInput {
  resourceId: string
  /** Моя работа за кассой — из нашей базы (`sessions/mine`): журнал не зависит от ответа кабинета. */
  mySession: QueryResultLike<AqsiSession | null>
  page: QueryResultLike<AqsiIncidentPage> | undefined
  view: IncidentsView
}

export const makeSelectIncidentsVm = () => {
  const selectRegistersView = makeQueryViewSelector(
    (input: IncidentsVmInput) => input.mySession,
    () => false,
  )
  const selectPageView = makeQueryViewSelector(
    (input: IncidentsVmInput) => input.page,
    (data) => data.items.length === 0,
  )
  const selectRows = createSelector(
    [selectPageView, (input: IncidentsVmInput) => input.page?.data],
    (view, data) => (readyData(view, data)?.items ?? []).map(incidentRow),
  )
  return createSelector(
    [
      (input: IncidentsVmInput) => input.resourceId,
      (input: IncidentsVmInput) => input.mySession.data,
      (input: IncidentsVmInput) => input.page !== undefined,
      (input: IncidentsVmInput) => input.page?.data,
      (input: IncidentsVmInput) => input.view,
      selectRegistersView,
      selectPageView,
      selectRows,
    ],
    (resourceId, sessionData, hasPage, pageData, view, registersQuery, pageQuery, rows): IncidentsVm => {
      const session = readyData(registersQuery, sessionData)
      const seat = incidentsSeatOf(session)
      const page = hasPage ? readyData(pageQuery, pageData) : undefined
      const error = hasPage ? pageQuery.error : null
      return {
        resourceId,
        seat,
        registersQuery,
        query: hasPage ? pageQuery : null,
        rows,
        page: view.page,
        canPrev: view.page > 0,
        canNext: page?.nextCursor != null,
        nextCursor: page?.nextCursor ?? null,
        pageText:
          page === undefined
            ? ''
            : `Страница ${view.page + 1}${page.complete ? ' — последняя' : ''}; снимок на ${formatInstant(page.observedAt)}, по ${INCIDENTS_PAGE_SIZE} строк`,
        cursorProblem: error !== null && CURSOR_ERROR_CODES.has(error.code),
      }
    },
  )
}

// ——— доказательство восстановления ———

const OUTCOME: Readonly<Record<string, Badge>> = {
  ReceiptExists: { text: 'чек есть — подтверждён кабинетом', tone: 'success', icon: 'ok' },
  NoDispatchConfirmed: { text: 'запрос чека кассе не уходил — доказано своим журналом', tone: 'neutral', icon: 'idle' },
  Unknown: { text: 'исход неизвестен — повтор чека запрещён', tone: 'warning', icon: 'unknown' },
}

const EVIDENCE: Readonly<Record<string, string>> = {
  OwnJournalNeverDispatched: 'Свой журнал: запрос кассе не отправлялся',
  ProviderNoEffectEvidence: 'Кабинет отказал до исполнения (не проверено вживую — не доказательство отсутствия чека)',
  ProviderReceiptMatch: 'Кабинет назвал чек этой операции',
  Unresolved: 'Попытка не разрешена: доказательства нет',
}

const NEXT: Readonly<Record<string, string>> = {
  Reconcile: 'Сверить операцию с кабинетом (чтение) — в окне продажи или возврата',
  Manual: 'Нужна ручная сверка с причиной: проверьте кассу и кабинет',
  None: 'Действий не требуется',
  ExplicitFiscalize: 'Чек можно пробить новой командой в окне продажи или возврата — отсюда ничего не отправляется',
}

export interface RecoveryProofVmInput {
  resourceId: string
  saleId: string
  proof: QueryResultLike<AqsiRecoveryProof>
}

export const makeSelectRecoveryProofVm = () => {
  const selectView = makeQueryViewSelector(
    (input: RecoveryProofVmInput) => input.proof,
    () => false,
  )
  return createSelector(
    [
      (input: RecoveryProofVmInput) => input.resourceId,
      (input: RecoveryProofVmInput) => input.saleId,
      (input: RecoveryProofVmInput) => input.proof.data,
      selectView,
    ],
    (resourceId, saleId, data, query): RecoveryProofVm => {
      const proof = readyData(query, data)
      if (proof === undefined) {
        return { resourceId, saleId, query, outcome: null, evidenceText: '', nextText: '', facts: [], manualOperationId: null }
      }
      return {
        resourceId,
        saleId,
        query,
        outcome: badgeOf(OUTCOME, 'Итог', proof.outcome),
        evidenceText: EVIDENCE[proof.evidenceKind] ?? proof.evidenceKind,
        nextText: NEXT[proof.nextAction] ?? proof.nextAction,
        facts: [
          { label: 'Продажа', value: proof.saleId },
          { label: 'Последняя попытка чека', value: proof.attemptId },
          { label: 'Первая попытка чека', value: proof.originalOperationId },
          { label: 'Чек', value: proof.receiptId ?? 'не назван' },
          { label: 'Операция кабинета', value: proof.providerOperationId ?? 'нет' },
          { label: 'По журналу (версия)', value: String(proof.basedOnJournalVersion) },
          { label: 'Наблюдалось', value: formatInstant(proof.observedAt) },
          { label: 'Правило проверки', value: proof.verificationPolicyVersion },
          { label: 'Отпечаток', value: proof.evidenceDigest },
          { label: 'Доказательство', value: proof.proofId },
        ],
        manualOperationId: proof.nextAction === 'Manual' ? proof.attemptId : null,
      }
    },
  )
}

// ——— журнал расхождений ———

/** Виды расхождений C4a (`AqsiDiscrepancyKinds`); неизвестный вид — как пришёл. */
const DISCREPANCY_KIND: Readonly<Record<string, string>> = {
  'amount-mismatch': 'Сумма чека кабинета ≠ сумме продажи',
  'direction-mismatch': 'Продажа и возврат перепутаны',
  'device-mismatch': 'Другая касса',
  'cabinet-content-changed': 'Кабинет изменил сохранённый чек',
  'cabinet-row-invalid': 'Строка кабинета не по формату',
}

export interface DiscrepanciesVmInput {
  resourceId: string
  limit: number
  query: QueryResultLike<AqsiDiscrepancies>
}

export const makeSelectDiscrepanciesVm = () => {
  const selectView = makeQueryViewSelector(
    (input: DiscrepanciesVmInput) => input.query,
    (data) => data.items.length === 0,
  )
  return createSelector(
    [
      (input: DiscrepanciesVmInput) => input.resourceId,
      (input: DiscrepanciesVmInput) => input.limit,
      (input: DiscrepanciesVmInput) => input.query.data,
      selectView,
    ],
    (resourceId, limit, data, query): DiscrepanciesVm => {
      const ready = readyData(query, data)
      const rows = (ready?.items ?? []).map((item) => ({
        id: item.id,
        kindText: DISCREPANCY_KIND[item.kind] ?? item.kind,
        detail: item.detail,
        receiptText: item.receiptId ?? 'чек не указан',
        saleText: item.saleId ?? 'локальной продажи нет',
        localText: item.localValue ?? 'нет',
        externalText: item.externalValue ?? 'нет',
        detectedText: formatInstant(item.detectedAt),
      }))
      return {
        resourceId,
        query,
        rows,
        limit,
        limitOptions: DISCREPANCY_LIMITS,
        caption:
          ready === undefined
            ? ''
            : rows.length >= ready.limit
              ? `Показаны последние ${ready.limit}: есть и более ранние — увеличьте предел`
              : `Всего расхождений: ${rows.length}`,
      }
    },
  )
}
